import React, {
  ChangeEvent,
  InputHTMLAttributes,
  memo,
  useCallback,
} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import { Iconify } from "../iconify";
import { styled } from "@mui/material";
import FileItem from "./components/file-item";
import { toast } from "sonner";
import { isVideoFile } from "@onair/utils/upload-to-s3";

export type UploadButtonProps = InputHTMLAttributes<HTMLInputElement> & {
  error?: boolean;
  onChange?: (files: (File | { fileName: string; fileUrl: string })[]) => void;
  helperText?: React.ReactNode;
  value?: (File | { fileName: string; fileUrl: string })[];
  multiple?: boolean;
  maxFileSize?: number;
  fileLimit?: number;
};

const UploadButton = React.forwardRef<HTMLInputElement, UploadButtonProps>(
  (
    {
      value,
      error,
      helperText,
      multiple = true,
      onChange,
      placeholder,
      maxFileSize,
      fileLimit,
      accept = "image/*, .pdf, .doc, .docx, .txt",
      disabled,
      ...restProps
    },
    ref,
  ) => {
    const handleFileChange = (evt: ChangeEvent<HTMLInputElement>) => {
      const fileList = Array.from(evt.target.files || []);

      if (!fileList.length) {
        toast.error(`Chưa có file nào được chọn.`);
        return;
      }

      if (fileLimit && fileList.length > fileLimit) {
        toast.error(`Chỉ được chọn tối đa ${fileLimit} files.`);
        return;
      }

      onChange?.([...(value || []), ...fileList]);
    };

    const handleRemoveFile = useCallback(
      (fileIndex: number) => {
        const newFiles =
          value?.filter((f, _index) => fileIndex !== _index) || [];
        onChange?.(newFiles);
      },
      [value],
    );
    const checkFileSize = (
      file: File | { fileName: string; fileUrl: string },
    ) => {
      if (!maxFileSize) return true;

      if (file instanceof File) {
        const maxFileSizeInBytes = maxFileSize * 1024 * 1024;
        if (file.size > maxFileSizeInBytes) {
          toast.error(`Dung lượng file tối đa ${maxFileSize} MB`);
          return false;
        }
      }

      return true;
    };
    isVideoFile;
    return (
      <Box sx={{ width: 1, position: "relative" }}>
        <Box>
          <Button
            component="label"
            role={undefined}
            variant="outlined"
            tabIndex={-1}
            startIcon={<Iconify icon="eva:cloud-upload-fill" />}
            disabled={disabled}
          >
            Tải lên
            <VisuallyHiddenInput
              ref={ref}
              multiple={multiple}
              accept={accept}
              {...restProps}
              type="file"
              onChange={handleFileChange}
            />
          </Button>

          {value?.length ? (
            <Box
              component="div"
              className="file-list gap-y-2 flex flex-col mt-6"
            >
              {value.map((file, _index) => (
                <FileItem
                  key={_index}
                  file={file}
                  isValid={checkFileSize(file)}
                  onRemove={() => handleRemoveFile(_index)}
                />
              ))}
            </Box>
          ) : null}
        </Box>
        {helperText && (
          <FormHelperText error={!!error} sx={{ px: 2 }}>
            {helperText}
          </FormHelperText>
        )}
      </Box>
    );
  },
);

export default memo(UploadButton);

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
